import { useEffect, useRef, useCallback } from "react";
import { EditorView, placeholder as cmPlaceholder, keymap } from "@codemirror/view";
import { EditorState, Compartment } from "@codemirror/state";
import { defaultKeymap, historyKeymap, history } from "@codemirror/commands";
import { qlikExpressionLanguage } from "@/lib/codemirror/qlik-expression-lang";
import { qlikThemeExtension } from "@/lib/codemirror/qlik-expression-theme";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { Check, AlertCircle } from "lucide-react";

interface ValidationState {
  status: "valid" | "invalid" | "idle";
  message?: string;
}

interface QlikExpressionEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  readOnly?: boolean;
  className?: string;
  id?: string;
  /**
   * Accessible name of the editing surface (the field label). A `<label for>`
   * cannot name a contenteditable, so the label text goes here; falls back to the placeholder.
   */
  ariaLabel?: string;
  /** Id of the element that names the field (a visible label); wins over `ariaLabel`. */
  ariaLabelledBy?: string;
  /** Id of the element that describes the field (a hint), read after its name. */
  ariaDescribedBy?: string;
  ariaInvalid?: boolean;
  /** Optional validation feedback rendered as a footer row. */
  validation?: ValidationState;
  /** When true, renders a small "fx" affordance in the top-right corner. */
  showFx?: boolean;
}

export function QlikExpressionEditor({
  value,
  onChange,
  placeholder,
  minHeight = "160px",
  readOnly = false,
  className,
  id,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  ariaInvalid,
  validation,
  showFx = false,
}: QlikExpressionEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  // Guards against the "sync external value" effect (below) echoing back through
  // onChange — i.e. selecting a different item must not register as a user edit.
  const isExternalUpdate = useRef(false);
  const readOnlyCompartment = useRef(new Compartment());
  const themeCompartment = useRef(new Compartment());
  const attributesCompartment = useRef(new Compartment());
  const contentAttributes: Record<string, string> = {
    ...(id ? { id } : {}),
    ...(ariaLabelledBy
      ? { "aria-labelledby": ariaLabelledBy }
      : ariaLabel || placeholder
        ? { "aria-label": ariaLabel ?? placeholder ?? "" }
        : {}),
    ...(ariaDescribedBy ? { "aria-describedby": ariaDescribedBy } : {}),
    ...(ariaInvalid ? { "aria-invalid": "true" } : {}),
  };
  const { resolvedTheme } = useTheme();

  // Keep onChange ref up-to-date without recreating the editor
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!containerRef.current) return;

    const updateListener = EditorView.updateListener.of((update) => {
      if (update.docChanged && !isExternalUpdate.current) {
        onChangeRef.current(update.state.doc.toString());
      }
    });

    const extensions = [
      history(),
      keymap.of([...defaultKeymap, ...historyKeymap]),
      qlikExpressionLanguage,
      themeCompartment.current.of(qlikThemeExtension(resolvedTheme === "dark")),
      updateListener,
      EditorView.lineWrapping,
      readOnlyCompartment.current.of(EditorState.readOnly.of(readOnly)),
      attributesCompartment.current.of(EditorView.contentAttributes.of(contentAttributes)),
    ];

    if (placeholder) {
      extensions.push(cmPlaceholder(placeholder));
    }

    const state = EditorState.create({
      doc: value,
      extensions,
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value changes (e.g. when form resets)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const current = view.state.doc.toString();
    if (current !== value) {
      isExternalUpdate.current = true;
      view.dispatch({
        changes: { from: 0, to: current.length, insert: value },
      });
      isExternalUpdate.current = false;
    }
  }, [value]);

  // Update readOnly when prop changes
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: readOnlyCompartment.current.reconfigure(EditorState.readOnly.of(readOnly)),
    });
  }, [readOnly]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: themeCompartment.current.reconfigure(qlikThemeExtension(resolvedTheme === "dark")),
    });
  }, [resolvedTheme]);

  // The label follows the language without rebuilding the editor.
  const label = contentAttributes["aria-label"];
  const labelledBy = contentAttributes["aria-labelledby"];
  const describedBy = contentAttributes["aria-describedby"];
  const invalid = contentAttributes["aria-invalid"];
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: attributesCompartment.current.reconfigure(
        EditorView.contentAttributes.of({
          ...(id ? { id } : {}),
          ...(label ? { "aria-label": label } : {}),
          ...(labelledBy ? { "aria-labelledby": labelledBy } : {}),
          ...(describedBy ? { "aria-describedby": describedBy } : {}),
          ...(invalid ? { "aria-invalid": invalid } : {}),
        }),
      ),
    });
  }, [id, label, labelledBy, describedBy, invalid]);

  const handleClick = useCallback(() => {
    viewRef.current?.focus();
  }, []);

  const showFooter = validation !== undefined && validation.status !== "idle";

  return (
    <div
      className={cn(
        "rounded-input overflow-hidden border border-transparent font-mono text-[13px]",
        "shadow-[inset_0_0_0_1px_var(--color-hairline)]",
        "focus-within:shadow-[inset_0_0_0_1.5px_var(--color-ink)]",
        className,
      )}
      onClick={handleClick}
    >
      {/* Editor area — positioned so the fx badge can be placed relative to it */}
      <div className="relative">
        <div ref={containerRef} style={{ minHeight }} />
        {showFx && (
          <span
            className="pointer-events-none absolute right-2.5 top-2 font-mono text-[11px] text-faint select-none"
            aria-hidden="true"
          >
            fx
          </span>
        )}
      </div>

      {/* Validation footer */}
      {showFooter && (
        <div
          className={cn(
            "flex items-center gap-1.5 border-t border-hairline-soft px-3 py-1.5 font-mono text-[11.5px]",
            validation.status === "valid" && "text-status-success-foreground",
            validation.status === "invalid" && "text-status-danger-foreground",
          )}
        >
          {validation.status === "valid" ? (
            <Check size={13} strokeWidth={1.8} className="shrink-0" />
          ) : (
            <AlertCircle size={13} strokeWidth={1.8} className="shrink-0" />
          )}
          {validation.message && (
            <span className="font-mono text-[11.5px]">{validation.message}</span>
          )}
        </div>
      )}
    </div>
  );
}
